<template>
  <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-md p-5 h-full w-full">
    <AppTooltip
      content="Settled and pending positions in your account will be displayed as distinct positions. Percents are rounded to the nearest decimal."
      placement="right"
      :width="225"
    >
      <div class="flex items-center gap-1">
        <div class="font-semibold text-lg">Positions</div>
        <InformationCircleIcon class="size-5 mt-0.5 text-gray-500" />
      </div>
    </AppTooltip>

    <div class="flex gap-5">
      <div class="max-h-80 p-4">
        <Doughnut ref="positionsPieChart" :data="chart_data" :options="chart_options" />
      </div>
      <div class="flex flex-col gap-2">
        <div v-for="label in static_labels" :key="label.name" class="flex items-center gap-x-2 flex-wrap">
          <!-- The small circle will have the background color from the label's color -->
          <div :style="{ backgroundColor: label.color }" class="rounded-full size-4"></div>

          <!-- Display the name of the label -->
          <div class="font-medium">{{ label.name }}</div>

          <!-- Display the percent of the total -->
          <div class="text-gray-600">{{ label.percent }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
// Essentials
import { ref, onMounted, watch } from "vue";
import { Doughnut } from "vue-chartjs";

// Components
import AppTooltip from "@/components/applicationui/AppTooltip.vue";

// Icons
import { InformationCircleIcon } from "@heroicons/vue/24/outline";

// Stores
import { useTransactionsStore } from "@/stores/transactions";
const transactionsStore = useTransactionsStore();

// Props
const props = defineProps({
  accounts: {
    type: Array,
    required: true,
  },
});

// Set Positions START
const unique_positions = ref([]);
async function setPositions() {
  unique_positions.value = [];
  // Get all account positions
  for (const account of props.accounts) {
    await setForAccountPositions(account.id);
  }

  const position_labels = [];
  const position_values = [];
  for (const position of unique_positions.value) {
    position_labels.push(position.id);
    position_values.push(position.total_amount);
  }

  return { labels: position_labels, data: position_values };
}

async function setForAccountPositions(account_id) {
  // Get holding account positions
  const transactions = await transactionsStore.getAllTransactionByAccountId(account_id);
  if (!transactions) {
    // TODO, handle error
    return;
  }

  
  // Set unique positions
  for (const transaction of transactions) {
    if (transaction.status == "canceled") continue;
    if (transaction.status == "refunded") continue;
    if (transaction.status == "disapproved") continue;
    if (transaction.status == "archived") continue;
    const transaction_total = transaction.amount * transaction.price_per_unit;
    if (transaction_total <= 0) continue;

    if (transaction.status == "settled") {
      if (["deposit", "refund", "distribution"].includes(transaction.type)) {
        const settled_cash_position = unique_positions.value.find((position) => position.id === "Settled Cash");
        if (settled_cash_position) settled_cash_position.total_amount += transaction_total;
        else unique_positions.value.push({ id: "Settled Cash", total_amount: transaction_total });
      }
      if (["withdraw"].includes(transaction.type)) {
        const settled_cash_position = unique_positions.value.find((position) => position.id === "Settled Cash");
        if (settled_cash_position) settled_cash_position.total_amount -= transaction_total;
        else unique_positions.value.push({ id: "Settled Cash", total_amount: 0 - transaction_total });
      }
      if (["buy"].includes(transaction.type)) {
        const unique_position = unique_positions.value.find((position) => position.id === transaction.offerings.name);
        if (unique_position) unique_position.total_amount += transaction_total;
        else unique_positions.value.push({ id: transaction.offerings.name, total_amount: transaction_total });

        const settled_cash_position = unique_positions.value.find((position) => position.id === "Settled Cash");
        if (settled_cash_position) settled_cash_position.total_amount -= transaction_total;
        else unique_positions.value.push({ id: "Settled Cash", total_amount: -transaction_total });
      }

      if (["sell", "refund"].includes(transaction.type)) {
        const unique_position = unique_positions.value.find((position) => position.id === transaction.offerings.name);
        if (unique_position) unique_position.total_amount -= transaction_total;
        else unique_positions.value.push({ id: transaction.offerings.name, total_amount: 0 - transaction_total });
      }
    } else {
      // covers status 'pending', 'refund_pending', 'funded'
      if (["deposit", "refund", "distribution"].includes(transaction.type)) {
        const pending_cash_position = unique_positions.value.find((position) => position.id === "Cash (Pending)");
        if (pending_cash_position) pending_cash_position.total_amount += transaction_total;
        else unique_positions.value.push({ id: "Cash (Pending)", total_amount: transaction_total });
      }

      if (transaction.type === "withdraw") {
        const pending_cash_position = unique_positions.value.find((position) => position.id === "Settled Cash");
        if (pending_cash_position) pending_cash_position.total_amount -= transaction_total;
        else unique_positions.value.push({ id: "Settled Cash", total_amount: -transaction_total });
      }

      if (transaction.type === "buy") {
        const unique_position = unique_positions.value.find((position) => position.id === `${transaction.offerings.name} (Pending)`);
        if (unique_position) unique_position.total_amount += transaction_total;
        else unique_positions.value.push({ id: `${transaction.offerings.name} (Pending)`, total_amount: transaction_total });

        const pending_cash_position = unique_positions.value.find((position) => position.id === "Cash (Pending)");
        if (pending_cash_position) pending_cash_position.total_amount -= transaction_total;
        else unique_positions.value.push({ id: "Cash (Pending)", total_amount: -transaction_total });
      }

      if (["sell", "refund"].includes(transaction.type)) {
        const unique_position = unique_positions.value.find((position) => position.id === `${transaction.offerings.name} (Pending)`);
        if (unique_position) unique_position.total_amount -= transaction_total;
        else unique_positions.value.push({ id: `${transaction.offerings.name} (Pending)`, total_amount: 0 - transaction_total });
      }
    }
  }
}
// Set Positions END

// Draw Pie Chart START
const static_labels = ref([]);
const chart_data = ref({
  labels: [],
  datasets: [
    {
      borderWidth: 3,
      label: "Positions",
      data: [],
      hoverOffset: 4,
      borderColor: "white", // Creates spacing between sections
      borderRadius: 5, // Rounds the corners
    },
  ],
});
const operacolor = getComputedStyle(document.documentElement).getPropertyValue("--operacolor");
const colors = [
  operacolor,
  "#EC4899", // pink 600
  "#06B6D4", // cyan 600
  "rgb(75, 192, 192)",
  "rgb(153, 102, 255)",
  "rgb(255, 159, 64)",
];
const specialColors = {
  "Settled Cash": "green",
  "Cash (Pending)": "lightgreen",
};
function drawPieChart(labels, data) {
  // Calculate the total sum of the data
  const total = data.reduce((sum, value) => sum + value, 0);

  // Set the static labels with name, percent, and color
  static_labels.value = labels.map((label, index) => {
    const percent = (data[index] / total) * 100;
    const color = specialColors[label] ?? colors[index % colors.length];
    return {
      name: label,
      percent: percent.toFixed(2) + "%", // Format percent to two decimal places
      color: color,
    };
  });

  // Set the background colors
  const backgroundColors = static_labels.value.map((labelObj) => labelObj.color);

  // Set the border color
  const theme = localStorage.theme;
  let border_color = "#ffffff";
  if (theme === "dark") border_color = "#404040";

  // Set the chart data
  const datasets = [
    {
      clip: 0,
      borderColor: border_color,
      borderWidth: 3,
      label: "Positions",
      data,
      backgroundColor: backgroundColors,
      hoverOffset: 4,
      borderRadius: 5, // Rounds the corners
    },
  ];
  chart_data.value = { labels, datasets };
}
const chart_options = {
  responsive: true,
  maintainAspectRatio: true, // Allows the chart to fill the container dynamically
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      callbacks: {
        label: function (tooltipItem) {
          const value = tooltipItem.raw;
          return ` $${value.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
        },
      },
    },
  },
  elements: {
    arc: {
      hoverBorderWidth: 0, // Removes the border on hover
    },
  },
};
// Draw Pie Chart END

// Mounted START
onMounted(async () => {
  const { labels, data } = await setPositions();
  drawPieChart(labels, data);
});
// Mounted END

// Watchers
watch(
  () => props.accounts,
  async () => {
    const { labels, data } = await setPositions();
    drawPieChart(labels, data);
  },
);
</script>
